import * as React from 'react'
import { TextField } from '../ui/TextField'
import { Button, ButtonText } from '../ui/button'
import { useForm, useController, useWatch } from 'react-hook-form'
import { useEffect, useState } from 'react'
import useAppStore from '@/store/useAppStore'
import useGetIncomeCategories from '@/services/api/transaction-category/useGetIncomeCategories'
import useGetExpenseCategories from '@/services/api/transaction-category/useGetExpenseCategories'
import useCreateTransactionCategory from '@/services/api/transaction-category/useCreateTransactionCategory'
import NonModalWindow from '@/components/NonModalWindow'
import useGetVaults from '@/services/api/vault/useGetVaults'
import { Description } from '../ui/Description'
import SimpleComboBox from '../SimpleComboBox'
import _ from 'lodash'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ConfirmationModal from '../ConfirmationModal'

export interface TransactionCategoryValues {
  name: string
  description?: string
  default_vault: any
  transaction_category_group: {
    id: number
    transaction_category_group_name: string
  }
}

export default function AddTransactionCategoryForm() {
  const navigate = useNavigate()
  const [newGroupIsOpen, setNewGroupIsOpen] = useState(false)
  const [isFocusedName, setIsFocusedName] = useState(false)
  const [isFocusedDescription, setIsFocusedNameDescription] = useState(false)
  const [isFormDirty, setIsFormDirty] = React.useState(false)
  const [openConfirmation, setOpenConfirmation] = React.useState(false)
  const {
    setError,
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<TransactionCategoryValues>({
    defaultValues: {},
  })
  const [searchParams] = useSearchParams()
  const type = searchParams.get('type') //in|out
  const nonModalWindowType = type === 'in' ? 'income' : 'expenses'

  const toggleNonModalWindow = useAppStore(
    (state) => state.toggleNonModalWindowState,
  )
  const { data: incomeCategories } = useGetIncomeCategories()
  const { data: expenseCategories } = useGetExpenseCategories()
  const { data: vaults } = useGetVaults()
  const { createTransactionCategory } = useCreateTransactionCategory({
    transactionCategoryType: nonModalWindowType,
    setError,
  })

  const categoryGroups = (data) =>
    data.filter(
      (expense) => expense.entity_type === 'transaction_category_group',
    )

  const vaultsFlat = _.flatten(
    vaults?.map((vault) => {
      return vault.is_group ? vault.vaults : [vault]
    }),
  ).filter((vault) => !vault.is_archived)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  React.useEffect(() => {
    setValue('name', name)
  }, [name])

  const handleNameChange = (event) => {
    const value = event.target.value
    if (value.length <= 30) {
      setName(value)
    }
  }

  React.useEffect(() => {
    setValue('description', description)
  }, [description])

  const handleDescriptionChange = (event) => {
    const value = event.target.value
    if (value.length <= 40) {
      setDescription(value)
    }
  }

  const { field: groupField } = useController({
    control: control,
    name: 'transaction_category_group.id',
    shouldUnregister: true,
  })

  const { field: defaultVaultField } = useController({
    control: control,
    name: 'default_vault',
    shouldUnregister: true,
  })

  useEffect(() => {
    if (name || description || groupField.value) {
      setIsFormDirty(true)
    } else {
      setIsFormDirty(false)
    }
  }, [name, description, groupField.value])

  const handleClose = () => {
    if (isFormDirty) {
      setOpenConfirmation(true)
    } else {
      navigate('/')
    }
  }

  const groupName = useWatch({
    control,
    name: 'transaction_category_group.transaction_category_group_name',
  })

  const onSubmit = async (data) => {
    const { default_vault } = data
    const groupId = data.transaction_category_group?.id?.id ?? null
    await createTransactionCategory({
      ...data,
      transaction_category_group: {
        id: groupId,
        transaction_category_group_name: groupId
          ? null
          : data.transaction_category_group?.transaction_category_group_name ??
            null,
      },
      default_vault_id: default_vault ? default_vault.id : null,
    })
    toggleNonModalWindow()
    navigate('/')
  }

  return (
    <>
      <ConfirmationModal
        type={
          (nonModalWindowType === 'income' && 'Add Money In Category') ||
          (nonModalWindowType === 'expenses' && 'Add Money Out Category')
        }
        open={openConfirmation}
        setOpen={setOpenConfirmation}
      />
      <form
        noValidate
        className=" flex flex-col"
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(onSubmit)
        }}
      >
        <NonModalWindow
          onSubmit={handleSubmit(onSubmit)}
          title={
            (nonModalWindowType === 'income' && 'Add Money In Category') ||
            (nonModalWindowType === 'expenses' && 'Add Money Out Category')
          }
          onClose={() => handleClose()}
          isDirty={isFormDirty}
        >
          <NonModalWindow.Body className="min-h-[78vh]">
            <p className="mb-3 text-sm text-txt-secondary">General</p>
            <div className="mb-3">
              <TextField
                status={errors?.name ? 'error' : null}
                statusText={errors.name?.message}
              >
                <TextField.Label className="flex justify-between text-sm">
                  <span>Category Name</span>
                  <span
                    className={`text-txt-secondary ${isFocusedName ? '' : 'hidden'}`}
                  >
                    {name.length}/30
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={30}
                  placeholder="e.g., Revenue"
                  {...register('name', {
                    required: 'Please fill out this field',
                    value: name,
                    onChange: handleNameChange,
                  })}
                  onFocus={() => setIsFocusedName(true)}
                  onBlur={() => setIsFocusedName(false)}
                />
              </TextField>
            </div>
            <div className="mb-3">
              <TextField>
                <TextField.Label className="flex justify-between text-sm">
                  <span>Description</span>
                  <span
                    className={`text-txt-secondary ${isFocusedDescription ? '' : 'hidden'}`}
                  >
                    {description ? description.length : '0'}/40
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={40}
                  placeholder="e.g., Core Business"
                  {...register('description', {
                    value: description,
                    onChange: handleDescriptionChange,
                  })}
                  onFocus={() => setIsFocusedNameDescription(true)}
                  onBlur={() => setIsFocusedNameDescription(false)}
                />
              </TextField>
            </div>
            <div className="mb-4">
              <TextField>
                <TextField.Label className="text-sm">
                  Vault for Transactions
                </TextField.Label>
                <SimpleComboBox
                  placeholder="Use Default"
                  items={vaultsFlat}
                  selected={defaultVaultField.value}
                  setSelected={defaultVaultField.onChange}
                  getOptionLabel={(item) => (
                    <div>
                      <p>{item.name}</p>
                      <Description>{item.description}</Description>
                    </div>
                  )}
                  getSelectedLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  clearable
                />
              </TextField>
            </div>

            <p className="mb-3 text-sm text-txt-secondary">
              Add to group(optional)
            </p>
            <div className="mb-3">
              <TextField>
                <TextField.Label className="text-sm">Group</TextField.Label>
                <SimpleComboBox
                  align="end"
                  placeholder="Please select a group for the vault"
                  items={
                    (nonModalWindowType === 'income' &&
                      categoryGroups(incomeCategories)) ||
                    (nonModalWindowType === 'expenses' &&
                      categoryGroups(expenseCategories)) ||
                    undefined
                  }
                  selected={groupField.value}
                  setSelected={(val) => {
                    setNewGroupIsOpen(false)
                    groupField.onChange(val)
                  }}
                  getOptionLabel={(item) => (
                    <div>
                      <p>{item.name}</p>
                      <Description>{item.description}</Description>
                    </div>
                  )}
                  getSelectedLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  getFixedOptionLabel={() => 'Create New Group'}
                  fixedOption={
                    <div
                      onClick={() => {
                        setNewGroupIsOpen(true)

                        groupField.onChange(null)
                      }}
                    >
                      Create New Group
                    </div>
                  }
                />
              </TextField>
            </div>
            {newGroupIsOpen && (
              <>
                <div className="group mb-3">
                  <TextField className="group">
                    <TextField.Label className="flex items-center justify-between text-sm">
                      <span>Group Name</span>
                      <span className="hidden text-txt-secondary group-focus-within:inline">
                        {groupName?.length ?? 0}/30
                      </span>
                    </TextField.Label>
                    <TextField.Input
                      maxLength={30}
                      placeholder="e.g., First Category Group"
                      {...register(
                        'transaction_category_group.transaction_category_group_name',
                      )}
                    />
                  </TextField>
                </div>
              </>
            )}
          </NonModalWindow.Body>
          <NonModalWindow.Footer>
            <Button
              variant="secondary"
              className="mr-2"
              onClick={() => handleClose()}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              <ButtonText>Add Category</ButtonText>
            </Button>
          </NonModalWindow.Footer>
        </NonModalWindow>
      </form>
    </>
  )
}
